import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Circle, Tooltip, useMap } from 'react-leaflet';
import { Box, Link, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import CircularProgressWithLabel from '../../components/CircularProgressWithLabel';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Loading from '../../components/Loading';
import IconButtonBox from '../../components/IconButtonBox';
import DashboardApi from '../../api/DashboardApi';
import { useError, useLoading } from '../../utils/functions';
import Draggable from 'react-draggable';

interface CountryData {
  name: string;
  lat: number;
  lng: number;
  count: number;
}

const Dashboard: React.FC = () => {
  const { setError } = useError();
  // const [isPanelVisible, setIsPanelVisible] = useState(false);
  const { startLoading, stopLoading, getLoadingStatus } = useLoading();
  const [countryData, setCountryData] = useState<CountryData[]>([]);
  const [formValues, setFormValues] = useState({
    quantity: 0,
    online: 0,
    offline: 0,
    connectionRate: 0,
  });

  const setValue = (field: string, value: any) => {
    setFormValues((prevValues) => ({ ...prevValues, [field]: value }));
    updateConnectionRate();
  };

  const updateConnectionRate = () => {
    setFormValues((prev: any) => {
      const newRate = prev.quantity > 0 ? ((prev.online / prev.quantity) * 100).toFixed(2) : 0;
      return { ...prev, connectionRate: newRate };
    });
  }

  useEffect(() => {
    const fetchData = async () => {
      startLoading();
      try {
        setCountryData([]);
        const dashBoardResponse = await DashboardApi.getDashBoard();
        if (dashBoardResponse.code) {
          setError(dashBoardResponse.message);
        } else {
          if (dashBoardResponse.connection) {
            setValue('online', dashBoardResponse.connection.online);
            setValue('offline', dashBoardResponse.connection.offline);
          }

          if (dashBoardResponse.map) {
            const newCountryData: CountryData[] = [];
            for (let i = 0; i < dashBoardResponse.map.length; i++) {
              const map = dashBoardResponse.map[i];
              newCountryData.push({
                name: map.countryCode,
                lat: map.coordinate[1],
                lng: map.coordinate[0],
                count: map.count,
              });
            }

            setValue('quantity', dashBoardResponse.total);
            setCountryData(newCountryData);
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        stopLoading();
      }
    };

    fetchData();
  }, []);


  // const showInfoBox = (count: any) => {
  //   // setValue('quantity', count);
  //   setIsPanelVisible(true);
  // }

  return (
    <>
      <Loading open={getLoadingStatus()} />
      {!getLoadingStatus() && (
        <Box sx={{ position: 'relative', height: '90vh', width: '100%' }}>

          <MapContainer
            center={[10, 10]}
            zoom={2}
            minZoom={2}
            maxBounds={[
              [-85, -180],
              [85, 180],
            ]}
            maxBoundsViscosity={1.0}
            style={{ height: "800px", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
            />
            {countryData.map((country, idx) => (
              <Circle
                key={idx}
                center={[country.lat, country.lng]}
                radius={Math.log(country.count + 1) * 50000}
                pathOptions={{ color: 'orange', fillColor: 'orange', fillOpacity: 0.4 }}
                // eventHandlers={{
                //   click: () => showInfoBox(country.count),
                // }}
              >
                <Tooltip>{`${country.name}: ${country.count} PCs`}</Tooltip>
              </Circle>
            ))}
          </MapContainer>

          {/* {isPanelVisible && ( */}
          <Draggable handle=".drag-handle">
            <Box
              padding={3}
              textAlign="center"
              bgcolor="white"
              className="drag-handle"
              sx={{
                position: "fixed",
                top: "100px",
                right: "40px",
                zIndex: 1300,
                width: { xs: "90%", sm: "40%", md: "25%" },
                maxWidth: "300px",
                minWidth: "250px",
                height: "auto",
                boxShadow: 3,
                borderRadius: 2,
              }}
            >
              {/* <Box
                sx={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  color: "gray",
                }}
              >
                <IconButtonBox
                  icon={<CloseIcon />}
                  onClick={() => setIsPanelVisible(false)} />
              </Box> */}

              <Typography variant="h6" sx={{ textAlign: "left", fontWeight: "bold" }}>
                Connection Rate
              </Typography>

              <List sx={{ display: "flex", padding: 0, mb: 2 }}>
                <ListItem sx={{ display: "flex", alignItems: "center", padding: 0, width: "auto", gap: 1 }}>
                  <ListItemIcon sx={{ minWidth: 0 }}>
                    <CircleIcon sx={{ fontSize: 10, color: "var(--primary-blue)" }} />
                  </ListItemIcon>
                  <ListItemText primary="Online" primaryTypographyProps={{ variant: "h6" }} />
                  <Typography variant="h6" className="primaryBlueColor">
                    {formValues.online}
                  </Typography>
                </ListItem>

                <ListItem sx={{ display: "flex", alignItems: "center", padding: 0, width: "auto", gap: 1, ml: 2 }}>
                  <ListItemIcon sx={{ minWidth: 0 }}>
                    <CircleOutlinedIcon sx={{ fontSize: 10, color: "var(--primary-blue)" }} />
                  </ListItemIcon>
                  <ListItemText primary="Offline" primaryTypographyProps={{ variant: "h6" }} />
                  <Typography variant="h6" sx={{ color: "gray" }}>
                    {formValues.offline}
                  </Typography>
                </ListItem>
              </List>

              <CircularProgressWithLabel thickness={5} value={formValues.connectionRate} size={100} />

              <Typography variant="h6" align="left" gutterBottom>
                Quantity
              </Typography>
              <Box display="flex" alignItems="baseline" mt={1} mb={3}>
                <Typography variant="h3" className="primaryBlueColor">
                  {formValues.quantity}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: "gray", ml: 1 }}>
                  PCs
                </Typography>
              </Box>
            </Box>
          </Draggable>
          {/* )} */}
        </Box>
      )}
    </>
  );
};

export default Dashboard;
